import { loadFont as loadSans } from '@remotion/google-fonts/PlusJakartaSans';
import { loadFont as loadSerif } from '@remotion/google-fonts/InstrumentSerif';
import { loadFont as loadMono } from '@remotion/google-fonts/SpaceMono';
import { loadFont as loadDmSerif } from '@remotion/google-fonts/DMSerifDisplay';
import { loadFont as loadBricolage } from '@remotion/google-fonts/BricolageGrotesque';
import { loadFont as loadCaveat } from '@remotion/google-fonts/Caveat';
import type { HeadingFont, VideoTheme } from './types';

const sans = loadSans('normal', { weights: ['400', '500', '600', '700', '800'], subsets: ['latin'] });
const serif = loadSerif('normal', { weights: ['400'], subsets: ['latin'] });
loadSerif('italic', { weights: ['400'], subsets: ['latin'] });
const mono = loadMono('normal', { weights: ['400', '700'], subsets: ['latin'] });

const dmSerif = loadDmSerif('normal', { weights: ['400'], subsets: ['latin'] });
loadDmSerif('italic', { weights: ['400'], subsets: ['latin'] });
const bricolage = loadBricolage('normal', { weights: ['800'], subsets: ['latin'] });
const caveat = loadCaveat('normal', { weights: ['700'], subsets: ['latin'] });

export const FONTS = { sans: sans.fontFamily, serif: serif.fontFamily, mono: mono.fontFamily };

/** Heading fonts the user can pick (Colors & font). Body text stays in the sans. */
export const HEADING_FONTS: { id: HeadingFont; name: string; meta: string; family: string; weight: number; serif: boolean }[] = [
    { id: 'serif', name: 'Instrument Serif', meta: 'Elegant · editorial', family: serif.fontFamily, weight: 400, serif: true },
    { id: 'dmserif', name: 'DM Serif Display', meta: 'Classic · bold serif', family: dmSerif.fontFamily, weight: 400, serif: true },
    { id: 'sans', name: 'Plus Jakarta Sans', meta: 'Modern · clean', family: sans.fontFamily, weight: 800, serif: false },
    { id: 'bricolage', name: 'Bricolage Grotesque', meta: 'Playful · punchy', family: bricolage.fontFamily, weight: 800, serif: false },
    { id: 'caveat', name: 'Caveat', meta: 'Handwritten · warm', family: caveat.fontFamily, weight: 700, serif: false },
];
const fontOf = (t: VideoTheme) => HEADING_FONTS.find((f) => f.id === t.headingFont) ?? HEADING_FONTS[2];

export const headingFamily = (t: VideoTheme) => fontOf(t).family;
export const headingWeight = (t: VideoTheme) => fontOf(t).weight;
/** Serif-style headings: tighter leading, italic emphasis. */
export const headingIsSerif = (t: VideoTheme) => fontOf(t).serif;

function rgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance(hex: string): number {
    const [r, g, b] = rgb(hex).map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Readable text color on top of `bg`. */
export const onColor = (bg: string, ink = '#111111') => (luminance(bg) > 0.45 ? ink : '#FFFFFF');

export const alpha = (hex: string, a: number) => {
    const [r, g, b] = rgb(hex);
    return `rgba(${r},${g},${b},${a})`;
};

export const isDark = (hex: string) => luminance(hex) < 0.2;
